// TabContent.js
import React from "react";
import ChartRenderer from "./ChartRenderer";
import "../styles/TabContent.css";
import generateChartData from "../../utils/generateChartData";
import KpiTable from "../KpiTable";
import CrosstabTable from "../../components/CrosstabTable";
import {
  exportPNG,
  exportPDF,
  exportXLSX,
  exportCSV,
  exportPPT,
  flattenCrosstab,
} from "../../utils/DownloadUtils";

export default function TabContent({
  selectedTab,
  updateKPI,
  updateFilter,
  updateFilterMultiple,
  removeFilter,
  removeKPI,
  filteredRows,
  toggleKPIViewMode,
  toggleShowTable,        // ✅ NEW
  crosstabDisplayData,
  setCrosstabDisplayData,
}) {
  if (!selectedTab) {
    return (
      <div className="tab-content-empty">
        <p>No tab selected</p>
      </div>
    );
  }

  const safeString = (v, fb = "") => {
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    if (!v) return fb;
    if (typeof v === "object") {
      return v.Name || v.name || v.label || v.title || fb;
    }
    return fb;
  };

  return (
    <div className="tab-content">
      <div className="tab-content-layout">
        {selectedTab.filters?.length > 0 && (
          <div className="filters-left">
            <h4 className="section-title">🎯 Filters</h4>

            <div className="filters-single-card">
              {selectedTab.filters.map((f) => {
                const filterLabel = safeString(
                  f.label,
                  f.variable_name || "Filter"
                );
                const isMulti = f.multiple === 1;
                const selectedArr = Array.isArray(f.selectedValue)
                  ? f.selectedValue
                  : f.selectedValue
                  ? [f.selectedValue]
                  : [];

                return (
                  <div key={f.id} className="filter-item">
                    <div className="filter-header">
                      <label className="filter-label">{filterLabel}</label>

                      <button
                        className="removefilter-btn"
                        onClick={() => removeFilter(selectedTab.id, f.id)}
                      >
                        ×
                      </button>
                    </div>

                    {/* Multi-select Checkbox */}
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        fontSize: "12px",
                        color: "#64748b",
                        marginBottom: "5px",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isMulti}
                        onChange={() =>
                          updateFilterMultiple && updateFilterMultiple(selectedTab.id, f.id)
                        }
                        style={{ width: "13px", height: "13px", cursor: "pointer", accentColor: "#2563eb" }}
                      />
                      Multi-select
                    </label>

                    {isMulti ? (
                      <select
                        multiple
                        className="filter-select"
                        value={selectedArr}
                        onChange={(e) => {
                          const chosen = Array.from(e.target.selectedOptions).map((o) => o.value);
                          updateFilter(selectedTab.id, f.id, chosen);
                        }}
                        style={{ minHeight: "90px" }}
                      >
                        {f.options?.map((opt, index) => {
                          let optionKey, optionValue, optionLabel;
                          if (typeof opt === "string") {
                            optionKey = optionValue = optionLabel = opt;
                          } else {
                            optionKey = opt.Id || opt.id || index;
                            optionValue = safeString(opt.Value || opt.value || opt.Name) || `value_${index}`;
                            optionLabel = safeString(opt.Name || opt.name || opt.label) || `Option ${index + 1}`;
                          }
                          return (
                            <option key={optionKey} value={optionValue}>
                              {optionLabel}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <select
                        className="filter-select"
                        value={
                          Array.isArray(f.selectedValue) ? (f.selectedValue[0] ?? "") : (f.selectedValue || "")
                        }
                        onChange={(e) =>
                          updateFilter(selectedTab.id, f.id, e.target.value)
                        }
                      >
                        <option value="">All</option>

                        {f.options?.map((opt, index) => {
                          let optionKey, optionValue, optionLabel;

                          if (typeof opt === "string") {
                            optionKey = optionValue = optionLabel = opt;
                          } else {
                            optionKey = opt.Id || opt.id || index;
                            optionValue =
                              safeString(opt.Value || opt.value || opt.Name) ||
                              `value_${index}`;
                            optionLabel =
                              safeString(opt.Name || opt.name || opt.label) ||
                              `Option ${index + 1}`;
                          }

                          return (
                            <option key={optionKey} value={optionValue}>
                              {optionLabel}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto" }}>
          {selectedTab.kpis?.length === 0 ? (
            <div className="tab-content-empty">
              <p>Add KPIs to visualize data.</p>
            </div>
          ) : (
            <div className="kpis-grid">
              {selectedTab.kpis.map((kpi) => {
                const kpiLabel = safeString(
                  kpi.label,
                  kpi.variableName,
                  kpi.qTitle || "KPI"
                );

                const isNumeric = !!kpi.isNumeric;
                const kpiData = kpi.data;

                return (
                  <div key={kpi.id} className="kpi-card">

                    {/* ── KPI Header ── */}
                    <div className="kpi-header">
                      <h5 className="kpi-title">
                        {kpi.qTitle && (
                          <span className="kpi-qtile">
                            {safeString(kpi.qTitle)}
                          </span>
                        )}
                      </h5>

                      <button
                        className="remove-btn"
                        onClick={() => removeKPI(selectedTab.id, kpi.id)}
                      >
                        ×
                      </button>
                    </div>

                    {/* ── KPI Controls ── */}
                    <div className="kpi-controls">
                      {!isNumeric && (
                        <>
                          <label className="control-label">Chart Type:</label>

                          <select
                            className="control-select"
                            value={kpi.type}
                            onChange={(e) =>
                              updateKPI(
                                selectedTab.id,
                                kpi.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            {kpi.isCrosstab ? (
                              <optgroup label="Crosstab Charts">
                                <option value="stackedcolumn">Stacked Column</option>
                                <option value="stackedbar">Stacked Bar</option>
                                <option value="groupedcolumn">Grouped Column</option>
                                <option value="groupedbar">Grouped Bar</option>
                                <option value="line">Line</option>
                              </optgroup>
                            ) : (
                              <>
                                <optgroup label="Basic Charts">
                                  <option value="bar">Bar</option>
                                  <option value="column">Column</option>
                                  <option value="line">Line</option>
                                  <option value="area">Area</option>
                                </optgroup>

                                <optgroup label="Stacked Charts">
                                  <option value="stackedbar">Stacked Bar</option>
                                  <option value="stackedcolumn">Stacked Column</option>
                                </optgroup>

                                <optgroup label="Circular Charts">
                                  <option value="pie">Pie</option>
                                  <option value="doughnut">Doughnut</option>
                                  <option value="radialbar">Radial Bar</option>
                                  <option value="radar">Radar</option>
                                </optgroup>

                                <optgroup label="Advanced Charts">
                                  <option value="scatter">Scatter</option>
                                  <option value="composed">Composed</option>
                                  <option value="funnel">Funnel</option>
                                </optgroup>
                              </>
                            )}
                          </select>
                        </>
                      )}

                      {/* ✅ Show Table Checkbox — non-numeric, non-crosstab KPIs ke liye */}
                      {!isNumeric && !kpi.isCrosstab && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#374151",
                            marginLeft: "auto",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!kpi.showTable}
                            onChange={() =>
                              toggleShowTable(selectedTab.id, kpi.id)
                            }
                            style={{
                              width: "14px",
                              height: "14px",
                              cursor: "pointer",
                              accentColor: "#2563eb",
                            }}
                          />
                          Show Table
                        </label>
                      )}

                      {/* ── Export Buttons ── */}
                      <div className="export-buttons">
                        <button
                          className="export-btn"
                          onClick={() =>
                            exportPDF(`kpi-${kpi.id}`, `${kpi.label}.pdf`)
                          }
                        >
                          <i className="ri-file-pdf-line"></i>
                          <span className="export-text">PDF</span>
                        </button>

                        <button
                          className="export-btn"
                          onClick={() =>
                            exportPPT(
                              `kpi-${kpi.id}`,
                              kpi.label,
                              `${kpi.label}.pptx`
                            )
                          }
                        >
                          <i className="ri-slideshow-line"></i>
                          <span className="export-text">PPT</span>
                        </button>

                        {/* XLSX + CSV — sirf tab jab table visible ho */}
                        {(kpi.showTable || kpi.isCrosstab) && (
                          <>
                            <button
                              className="export-btn"
                              onClick={() => {
                                const flat = kpi.isCrosstab
                                  ? flattenCrosstab({
                                      rows: kpiData.rowValues,
                                      columns: kpiData.colValues,
                                      matrix: kpiData.table,
                                    })
                                  : kpiData;
                                exportXLSX(flat, `${kpi.label}.xlsx`);
                              }}
                            >
                              <i className="ri-file-excel-line"></i>
                              <span className="export-text">XLSX</span>
                            </button>

                            <button
                              className="export-btn"
                              onClick={() => {
                                const flat = kpi.isCrosstab
                                  ? flattenCrosstab({
                                      rows: kpiData.rowValues,
                                      columns: kpiData.colValues,
                                      matrix: kpiData.table,
                                    })
                                  : kpiData;
                                exportCSV(flat, `${kpi.label}.csv`);
                              }}
                            >
                              <i className="ri-file-text-line"></i>
                              <span className="export-text">CSV</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* ── KPI Preview ── */}
                    <div id={`kpi-${kpi.id}`} className="kpi-preview">
                      {isNumeric ? (
                        // Numeric KPI — always table
                        <KpiTable data={kpiData} numericSummary />

                      ) : kpi.isCrosstab ? (
                        // Crosstab — chart + table via viewMode toggle (existing behavior)
                        kpi.viewMode === "chart" ? (
                          <ChartRenderer
                            type={kpi.type || "stackedcolumn"}
                            data={kpiData}
                            width="100%"
                            height={400}
                            isCrosstab={true}
                          />
                        ) : (
                          <CrosstabTable
                            crosstab={crosstabDisplayData.crosstabData}
                          />
                        )

                      ) : (
                        // Normal KPI — chart always + table conditionally
                        <>
                          {/* Chart — hamesha visible (default view) */}
                          {(() => {
                            const dynamicWidth = Math.max(
                              900,
                              (kpiData?.length || 1) * 120
                            );
                            return (
                              <ChartRenderer
                                type={kpi.type}
                                data={kpiData}
                                width={`${dynamicWidth}px`}
                                height={400}
                              />
                            );
                          })()}

                          {/* Table — sirf jab showTable checkbox true ho */}
                          {kpi.showTable && (
                            <div style={{ marginTop: "16px" }}>
                              <KpiTable data={kpiData} />
                            </div>
                          )}
                        </>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}